import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import autoBind from 'react-autobind';
import { connect } from 'react-redux';
import { translations } from '../../sharedJs/translations';
import SvgElement from '../uiElements/svgElement/SvgElement';
import LoadingSpinner from '../uiElements/loadingSpinner/LoadingSpinner';
import TextArea from '../formElements/textArea/TextArea';
import TextInput from '../formElements/textInput/TextInput';
import Button from '../buttons/button/Button';

import {
    setFeedbackBarOpen,
    sendFeedbackType,
    postFeedbackMessage,
    resetState
} from '../../actions/feedbackBarActions';
import { FEEDBACKBAR_TYPE_POSITIVE, FEEDBACKBAR_TYPE_NEGATIVE } from '../../actions/types';

import { bulkSetAttribute } from '../../sharedJs/utilityFunctions';
import '../../styles/_grid.scss';
import '../../styles/_utilities.scss';
import './_feedbackBar.scss';

const checkmark = '/assets/miljostatus/icons/checkmark-blue.svg';
const closeBlack = '/assets/miljostatus/icons/icon-close-black.svg';

class FeedbackBar extends Component {
    constructor() {
        super();
        autoBind(this);
        this.state = {
            maxHeight: 2000,
            name: '',
            email: '',
            message: '',
            emailIsValid: true,
            messageIsValid: true
        };
        this.barTop = null;

        this.setBarTopRef = (element) => {
            this.barTop = element;
        };

        this.feedbackContentRef = React.createRef();
    }

    componentDidMount() {
        this.props.resetState();
        setTimeout(() => {
            const contentElement = this.feedbackContentRef.current;
            let height = 0;
            if (contentElement) {
                height = contentElement.offsetHeight;
            }
            if (height === 0) {
                height = 2000;
            }
            this.setState({ maxHeight: height }); // eslint-disable-line
            this.props.setFeedbackBarOpen(false);
            const inputTags = contentElement.getElementsByTagName('input');
            const buttonTags = contentElement.getElementsByTagName('button');
            const taTags = contentElement.getElementsByTagName('textarea');
            bulkSetAttribute({ items: [...inputTags, ...buttonTags, ...taTags], attr: 'tabIndex', value: '-1' });
        }, 100);
    }

    componentDidUpdate() {
        const contentElement = this.feedbackContentRef.current;
        const inputTags = contentElement.getElementsByTagName('input');
        const taTags = contentElement.getElementsByTagName('textarea');
        const buttonTags = contentElement.getElementsByTagName('button');
        if (this.props.isOpen) {
            bulkSetAttribute({ items: [...inputTags, ...buttonTags, ...taTags], attr: 'tabIndex', value: '0' });
        } else {
            bulkSetAttribute({ items: [...inputTags, ...buttonTags, ...taTags], attr: 'tabIndex', value: '-1' });
        }
    }

    onYesButtonClick() {
        if (!this.props.feedbackTypeSent) {
            this.props.sendFeedbackType(FEEDBACKBAR_TYPE_POSITIVE, this.props.pageInfo);
        }
    }

    onNoButtonClick() {
        if (!this.props.feedbackTypeSent) {
            this.props.sendFeedbackType(FEEDBACKBAR_TYPE_NEGATIVE, this.props.pageInfo);
        }
        setTimeout(() => {
            this.barTop.scrollIntoView({ behavior: 'smooth' });
        }, 100);
    }

    onSubmitClick(e) {
        if (this.state.message.length >= 3 && this.state.emailIsValid) {
            this.props.postFeedbackMessage(
                this.state.name,
                this.state.email,
                this.state.message,
                this.props.pageInfo
            );
            this.barTop.scrollIntoView({ behavior: 'smooth' });
        } else {
            e.preventDefault();
            this.setState({ messageIsValid: false });
        }
    }

    onEmailInputChange(string) {
        this.setState({ email: string });
        if (string.length > 0) {
            this.setState({ emailIsValid: this.validateEmail(string) });
        } else {
            this.setState({ emailIsValid: true });
        }
    }

    onMessageInputChange(string) {
        this.setState({
            message: string,
            messageIsValid: string.length >= 3
        });
    }

    validateEmail(email) {
        const re = new RegExp(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);
        return re.test(String(email).toLowerCase());
    }

    closeContentContainer() {
        this.props.setFeedbackBarOpen(false);
    }

    renderMessageForm() {
        if (this.props.isLoading) {
            return <LoadingSpinner />;
        }
        const translate = translations(this.props.lang);
        return (
            <Fragment>
                <div className="g_row">
                    <div className="g_col  g_col--large-12">
                        <h2 className="u_hide-from-anchor-menu">{translate.FeedbackBar.GiveUsFeedback}</h2>
                    </div>
                </div>
                <div className="g_row">
                    <div className="g_col g_col--large-6 c_feedback-bar-content__left-col">
                        <TextArea
                            id="feedbackbarTextarea"
                            labelText={translate.FeedbackBar.WhatDidYouNotFind}
                            placeholder={translate.FeedbackBar.WhatDidYouNotFind}
                            hideLabelText
                            value={this.state.message}
                            onChange={e => this.onMessageInputChange(e.target.value)}
                            invalid={!this.state.messageIsValid}
                            invalidText={translate.FeedbackBar.PleaseWriteFeedbackHere}
                        />
                    </div>
                    <div className="g_col g_col--large-4 c_feedback-bar-content__right-col">
                        <p>{translate.FeedbackBar.IfYouWantResponse}</p>
                        <TextInput
                            id="feedbackbarNameInput"
                            labelText={translate.FeedbackBar.NameOptional}
                            value={this.state.name}
                            onChange={e => this.setState({ name: e.target.value })}
                        />
                        <TextInput
                            id="feedbackbarEmailInput"
                            labelText={translate.FeedbackBar.EmailOptional}
                            type="email"
                            value={this.state.email}
                            onChange={e => this.onEmailInputChange(e.target.value)}
                            invalid={!this.state.emailIsValid}
                            invalidText={translate.FeedbackBar.MissingCorrectEmail}
                        />
                    </div>
                </div>
                <div className="g_row">
                    <div className="g_col  g_col--large-12">
                        <Button
                            text="Send"
                            onClick={e => this.onSubmitClick(e)}
                        />
                    </div>
                </div>
            </Fragment>
        );
    }

    renderMessageConfirmation() {
        if (this.props.isLoading) {
            return <LoadingSpinner />;
        }
        const translate = translations(this.props.lang);
        return (
            <div className="c_message-confirmation">
                <div className="g_row">
                    <div className="g_col  g_col--large-12">
                        <h2>{translate.FeedbackBar.ThanksForTheFeedback}{this.state.name.length > 0 && `, ${this.state.name}`}</h2>
                    </div>
                </div>
                <div className="g_row">
                    <div className="g_col  g_col--large-12">
                        {this.state.email.length > 0 ?
                            <p>{translate.FeedbackBar.ConfirmationTextWithMail}</p>
                            :
                            <p>{translate.FeedbackBar.ConfirmationTextNoMail}</p>
                        }
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const {
            isOpen,
            isLoading,
            feedbackType,
            feedbackTypeSent,
            feedbackMessagePosted
        } = this.props;
        const translate = translations(this.props.lang);

        return (
            <div className="c_feedback-bar" ref={this.setBarTopRef}>
                <div className="c_feedback-bar-header">
                    <div className="g_container g_container--full">
                        <span className="c_feedback-bar-header__question">
                            {translate.FeedbackBar.DidYouFindWhatYouWhereLookingFor}
                        </span>
                        {feedbackType.toString() !== FEEDBACKBAR_TYPE_POSITIVE.toString() &&
                        <span className="c_feedback-bar-header__buttons-wrapper">
                            <button
                                onClick={() => this.onYesButtonClick()}
                                className={`c_feedback-bar-header__button ${
                                    feedbackType.toString() === FEEDBACKBAR_TYPE_POSITIVE.toString() ?
                                        'c_feedback-bar-header__button--clicked' : ''
                                }`}
                            >{translate.FeedbackBar.Yes}
                            </button>
                            <span>|</span>
                            <button
                                onClick={() => this.onNoButtonClick()}
                                className={`c_feedback-bar-header__button ${
                                    feedbackType.toString() === FEEDBACKBAR_TYPE_NEGATIVE.toString() ?
                                        'c_feedback-bar-header__button--clicked' : ''
                                }`}
                                aria-expanded={isOpen}
                            >{translate.FeedbackBar.No}
                            </button>
                        </span>
                        }
                        {isLoading && !feedbackTypeSent &&
                        <LoadingSpinner />
                        }
                        {!isLoading && feedbackTypeSent && !feedbackMessagePosted &&
                        <span className="c_feedback-bar-header__type-sent-confirmation">
                            {feedbackType.toString() !== FEEDBACKBAR_TYPE_POSITIVE.toString() ?
                                <Fragment>
                                    <SvgElement
                                        src={checkmark}
                                        title={translate.App.Checkmark}
                                        desc={translate.FeedbackBar.FeedbackTypeRegistered}
                                    />
                                    <span>{`${translate.FeedbackBar.No} - ${translate.FeedbackBar.Registered}`}</span>
                                </Fragment>
                                :
                                <span>{translate.FeedbackBar.ThanksForTheFeedback}!</span>
                            }
                        </span>
                        }
                    </div>
                </div>
                <div
                    id="feedbackBarContent"
                    className={`c_feedback-bar-content ${isOpen ?
                        'c_feedback-bar-content--expanded' : 'c_feedback-bar-content--collapsed'}`}
                    style={{ maxHeight: isOpen ? this.state.maxHeight : 0 }}
                    ref={this.feedbackContentRef}
                >
                    <div className="g_container g_container--full">
                        {!this.props.feedbackMessagePosted &&
                        <button
                            className="c_close-button u_hidden--mobile"
                            onClick={() => this.closeContentContainer()}
                        >
                            <span className="c_close-button__inner-wrapper">
                                <span className="c_close-button__text">{translate.App.Close}</span>
                                <SvgElement
                                    src={closeBlack}
                                    title={translate.App.Close}
                                    desc={translate.App.Close}
                                />
                            </span>
                        </button>
                        }
                        <div className="c_feedback-bar-content-container">
                            {!this.props.feedbackMessagePosted ?
                                this.renderMessageForm()
                                :
                                this.renderMessageConfirmation()
                            }
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

FeedbackBar.propTypes = {
    pageInfo: PropTypes.shape({
        pageUrl: PropTypes.string,
        pageTitle: PropTypes.string,
        category: PropTypes.object
    }).isRequired,
    isOpen: PropTypes.bool,
    isLoading: PropTypes.bool,
    feedbackType: PropTypes.string,
    feedbackTypeSent: PropTypes.bool,
    feedbackMessagePosted: PropTypes.bool,
    setFeedbackBarOpen: PropTypes.func,
    sendFeedbackType: PropTypes.func,
    postFeedbackMessage: PropTypes.func,
    resetState: PropTypes.func,
    lang: PropTypes.string
};

const mapStateToProps = state => ({
    isLoading: state.feedbackBar.isLoading,
    isOpen: state.feedbackBar.isOpen,
    feedbackType: state.feedbackBar.feedbackType,
    feedbackTypeSent: state.feedbackBar.feedbackTypeSent,
    feedbackMessagePosted: state.feedbackBar.feedbackMessagePosted
});

export default connect(mapStateToProps, {
    setFeedbackBarOpen,
    sendFeedbackType,
    postFeedbackMessage,
    resetState
})(FeedbackBar);
